import { ButtonCta, PageGroup, ResponsiveImage } from '@sleepstation/components'
import { BreakingLayout, VLayout } from '@sleepstation/components-layouts'
import { CopyRenderer } from '@sleepstation/components-markdown-renderer'
import {
  DIRECTION,
  LAYOUT_ALIGN,
  LAYOUT_CHILD,
  LAYOUT_JUSTIFY,
  LAYOUT_SPACING,
} from '@sleepstation/const'
import { parseToAst } from '@sleepstation/markdown'
import { mq, paddingTop } from '@sleepstation/style-api-helpers'
import { constrainWide } from '@sleepstation/style-mixins'
import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const IMAGE_SMALL_ASPECT_RATIO = 272 / 144
const IMAGE_LARGE_APSECT_RATIO = 588 / 420

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled.div.attrs({
  className: `Breakout`,
})`
  ${paddingTop([1, 3])}
`

const Body = styled(BreakingLayout).attrs({
  direction: [
    DIRECTION.VERTICAL_REVERSED,
    DIRECTION.VERTICAL_REVERSED,
    DIRECTION.HORIZONTAL,
  ],
  justify: LAYOUT_JUSTIFY.CENTER,
  align: LAYOUT_ALIGN.CENTER,
  spacing: [LAYOUT_SPACING.LARGE, 2, 3, 5],
  child: LAYOUT_CHILD.EQUAL,
})`
  ${constrainWide()}
`

const ImageWrapper = styled.div`
  width: 100%;

  > *:first-child {
    ${mq(`mediumUp`)} {
      display: none;
    }
  }

  > *:last-child {
    ${mq(`mediumDown`)} {
      display: none;
    }
  }
`

const Primary = styled(VLayout).attrs({
  align: [LAYOUT_ALIGN.STRETCH, LAYOUT_ALIGN.LEFT],
  spacing: LAYOUT_SPACING.LARGE,
})``

const Secondary = styled.div`
  position: relative;

  /* stylelint-disable-next-line */
  ${mq(`mediumDown`)} {
    width: 100%;
  }
`

const Footer = styled(VLayout).attrs({
  as: `footer`,
  align: [LAYOUT_ALIGN.STRETCH, LAYOUT_ALIGN.LEFT],
})`
  /* stylelint-disable-next-line */
  ${mq(`smallUp`)} {
    max-width: 50%;
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const BreakingImageHero = ({
  title,
  body,
  image,
  imageLarge,
  imageAlt,
  linkUrl,
  linkLabel,
}) => {
  const bodyAst = parseToAst(body)
  const hasButton = !!(linkUrl && linkLabel)

  return (
    <Layout>
      <Body>
        <Primary>
          <PageGroup title={title} spacing={[0.5, 0.75]}>
            <CopyRenderer htmlAst={bodyAst} />
            {hasButton && (
              <Footer>
                <ButtonCta linkUrl={linkUrl} linkLabel={linkLabel} />
              </Footer>
            )}
          </PageGroup>
        </Primary>
        <Secondary>
          <ImageWrapper>
            <ResponsiveImage
              aspectRatio={IMAGE_SMALL_ASPECT_RATIO}
              alt={imageAlt}
              src={image}
            />
            <ResponsiveImage
              aspectRatio={IMAGE_LARGE_APSECT_RATIO}
              alt={imageAlt}
              src={imageLarge}
            />
          </ImageWrapper>
        </Secondary>
      </Body>
    </Layout>
  )
}

BreakingImageHero.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  imageLarge: PropTypes.string.isRequired,
  imageAlt: PropTypes.string.isRequired,
  linkLabel: PropTypes.string,
  linkUrl: PropTypes.string,
}

BreakingImageHero.defaultProps = {
  linkLabel: null,
  linkUrl: null,
}

export default BreakingImageHero
